import { Injectable } from '@angular/core'
import { Select, State, Action, StateContext, Selector, NgxsOnInit, Store, NgxsSimpleChange } from '@ngxs/store'
import { Observable } from 'rxjs';
import { UserState } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { VehicleActions } from '../actions/vehicle.actions';
import { Vehicle } from '../models/vehicle.model';
import { VehicleService } from 'src/app/services/vehicle.service';
import { StorageService } from 'src/app/services/storage.service';
import { stateContentChanged } from './state-content-changed'

export class VehicleStateModel {
    is_refreshing: boolean
    vehicle: Vehicle[]
    version?: string
}

@State<VehicleStateModel>({
    name: 'vehicle',
    defaults: {
        is_refreshing: true,
        vehicle: []
    }
})
@Injectable()
export class VehicleState implements NgxsOnInit {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(        
        private vehicleService: VehicleService,
        private store: Store,
        private storageService: StorageService
    ) {}

    async ngxsOnInit(ctx: StateContext<VehicleStateModel>) {                
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {
                        await this.reloadStateFromStorage()                        
                    }
                })                           
            }
        }) 
    }

    async ngxsOnChanges(change: NgxsSimpleChange) {       
        if (this.storageService.is_initialized && stateContentChanged(change)) {
            const state_model: VehicleStateModel = change.currentValue
            this.storageService.set('vehicles', JSON.stringify(state_model))    
        }      
    }

    async reloadStateFromStorage() {        
        const json = await this.storageService.get('vehicles');        
        if (json) {
            const state_model: VehicleStateModel = JSON.parse(json)
            if (state_model) {
                this.store.dispatch(new VehicleActions.Init(state_model));
                return
            }
        }
        this.store.dispatch(new VehicleActions.Refresh())
    }

    async reloadStateFromApi() : Promise<boolean> {    
        // Send the version only with rows to go with it.
        const held = this.store.selectSnapshot(VehicleState.getState)
        const ret = await this.vehicleService.getAll(held.vehicle?.length ? held.version ?? '' : '')
        if (ret?.rows) {
          // The service has already run buildMany over the response, so the
          // rows arriving here are built models. Building them a second time
          // allocated a duplicate object for every row and, where buildOne
          // rebuilds children, re-walked those too.
          this.store.dispatch(new VehicleActions.Set(ret.rows, ret.version))
          return true
        }
        // Failed, or the list held is still current.
        return false
    }
  
    @Selector()
    static getState(state: VehicleStateModel) {
        return state
    }

    @Selector()
    static getIsRefreshing(state: VehicleStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getAll(state: VehicleStateModel) {
        return state.vehicle
    }

    @Action(VehicleActions.Refresh)
    async refresh({patchState}: StateContext<VehicleStateModel>, {  }:VehicleActions.Refresh) {
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi()) {            
            patchState({is_refreshing: false})
        }
    }

    @Action(VehicleActions.Init)
    init({setState, getState}: StateContext<VehicleStateModel>, { payload }:VehicleActions.Init) {                  
        const state = getState()
        payload.is_refreshing = state.is_refreshing
        setState(payload)
        this.store.dispatch(new VehicleActions.Refresh())
    }
    
    @Action(VehicleActions.Set)
    set({setState}: StateContext<VehicleStateModel>, { payload, version }:VehicleActions.Set) {             
        setState({       
            is_refreshing: false,     
            vehicle: payload,
            version
        })
    }
}
